import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { DialogService, GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { SessionTopicAssignment } from '@interfaces';
import { DomainDataService } from '../../../../../services';
import { VolunteeringAreaService } from '../../../volunteering-area.service';
import { VolunteeringSpecialistsService } from '../../volunteering-specialists.service';
import { getSetup } from './grid/get-setup';

export interface SessionTopicAssignmentByEvent extends SessionTopicAssignment {
	assignedSpecialistNames: string[], // This is an array of specialist names assigned to the event 
	topicName: string,
	pendingSignups: number,
	waitlistedSignups: number,
}

@Component({
	selector: 'session-assignments-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './session-assignments.part.html',
})
export class SessionAssignmentsViewPart implements OnInit {

	private readonly util = inject(UtilityService);
	private readonly domainData = inject(DomainDataService);
	public readonly areaService = inject(VolunteeringAreaService);
	private readonly pageService = inject(VolunteeringSpecialistsService);
	private readonly dialogService = inject(DialogService);

	public setup = getSetup(this.areaService, this.util, this.dialogService, this.areaService.app);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public assignments = computed(() => {
		const topicMap = this.domainData.data().topicsMap;
		if (!topicMap) return [];

		const data = this.areaService.volunteeringData();
		if (!data) return [];

		const signupCountKey = (topicId: number, languageId: number, startUTC: number, endUTC: number, trimesterId: number) => `${topicId}_${languageId}_${startUTC}_${endUTC}_${trimesterId}`;

		const signupCounts = data.signUpSpecialists.reduce((acc, s) => {
			const key = signupCountKey(s.topicId, s.languageId, s.availableClassStartTimeUTC, s.availableClassEndTimeUTC, s.trimesterId);
			const counts = (acc[key] ??= { pending: 0, waitlisted: 0 });

			if (s.volunteerSignupSpecialistStatus === 'Pending') counts.pending++;
			if (s.volunteerSignupSpecialistStatus === 'Waitlisted') counts.waitlisted++;

			return acc;
		}, {} as Record<string, { pending: number; waitlisted: number }>);

		return data.sessionTopicAssignments.map(assignment => {

			const counts = signupCounts[signupCountKey(assignment.topicId, assignment.languageId, assignment.startUTC, assignment.endUTC, data.selectedTrimester.trimesterId)] ?? { pending: 0, waitlisted: 0 };

			return {
				...assignment,
				topicName: topicMap[assignment.topicId]?.name ?? '',
				assignedSpecialistNames: assignment.assignedSpecialists.map(s => s.fullName),
				pendingSignups: counts.pending,
				waitlistedSignups: counts.waitlisted
			};
		}).sort((a, b) => a.startUTC - b.startUTC);

	});

	async ngOnInit() {
		await this.areaService.getVolunteeringData();
	}

}